import { EditOutlined, PrinterOutlined } from '@ant-design/icons';
import { Card, Button, Flex, TablePaginationConfig, TableProps, Space, Table } from 'antd';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import queryString from 'query-string';
import { useCallback, useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation, useParams } from 'react-router-dom';
import { Fragment } from 'react/jsx-runtime';
import { API } from '../../api';
import { ParcelStatusContext } from '../../context/ParcelStatusProvider';
import { PagedResultState } from '../../common/CommonResult';
import OrderSearch from '../../lib/OrderSearch';
import ParcelOrderPrint from '../../lib/parcel/ParcelOrderPrint';
import { orderDownload, parseSearchParam } from '../../store/orderUtils';
import { notifyError, notifySuccess, priceFormat } from '../../store/utils';
import { nextStatusList } from '../../common/delivery_status';
import ParcelOrderScanModal from '../../lib/parcel/ParcelOrderScanModal';
import { ParcelOrderScanStateType } from '../../common/scan';
import { LineItemScanType } from '../../common/lineItem';
import ParcelOrderSecondoryPrint from '../../lib/parcel/ParcelOrderSecondoryPrint';
import { UserContext } from '../../context/UserProvider';

const DeliveryView = () => {
  const location = useLocation();
  const { parcelStatusCd } = useParams();
  const { statusList } = useContext(ParcelStatusContext);
  const navigate = useNavigate();

  const onSerach = useCallback(
    async (query: any) => {
      navigate(`/delivery/${parcelStatusCd}?${queryString.stringify({ ...query, page: 1 })}`);
    },
    [parcelStatusCd]
  );

  const parcelStatus = useMemo(() => {
    return statusList.find((status: any) => status.cd === parcelStatusCd?.toUpperCase());
  }, [statusList, parcelStatusCd]);

  return (
    <Card title={`${parcelStatus?.name} баримтын жагсаалт`}>
      <Flex gap={24} vertical>
        <OrderSearch onSearch={(params: any) => onSerach(params)} />
        <DeliveryList />
      </Flex>
    </Card>
  );
};
export default DeliveryView;

const DeliveryList = () => {
  const { getCustomer } = useContext(UserContext);
  const { parcelStatusCd } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { statusList } = useContext(ParcelStatusContext);
  const [selected, setSelected] = useState<any[]>([]);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [print, setPrint] = useState<{ visible: boolean; orderIds: number[] }>({
    visible: false,
    orderIds: [],
  });
  const [scan, updateScan] = useState<ParcelOrderScanStateType>({ visible: false });
  const [secondory, setSecondory] = useState(false);

  const [state, setState] = useState<PagedResultState<any>>({
    loading: true,
    result: {},
  });

  const [disabled, setDisabled] = useState(false);

  const batchChangeOrder = useCallback(
    (status: string) => {
      const pStatus = statusList.find((item: any) => item.cd === status);
      if (pStatus) {
        const shipmentStatusReasons = pStatus?.shipmentStatusReasons || [];
        if (shipmentStatusReasons.length) {
          const items = selected.map((itm: any, index) => {
            return {
              index,
              barcode: itm.barcode,
              scanDate: dayjs(),
            };
          });
          updateScan({
            visible: true,
            items,
            parcelStatus: pStatus,
          });
        } else {
          const payload: LineItemScanType = {
            status,
            barcodeItems: selected.map((itm: any) => {
              return {
                barcode: itm.barcode,
              };
            }),
          };
          batchLineItemUpdate(payload);
        }
      } else {
        notifyError('Төлөв олдсонгүй');
      }
    },
    [selected, statusList]
  );

  const batchLineItemUpdate = useCallback(
    async (payload: LineItemScanType) => {
      try {
        setDisabled(true);
        const {
          result: { data },
        } = await API.post({ apiVersion: 'api' })(`/order/status`, payload);
        const message = `Нийт ${(data?.successBarcodeList || []).length + (data?.errorBarCodeList || []).length}ш илгээмж. Амжилттай ${(data?.successBarcodeList || []).length}ш. Алдаатай ${(data?.errorBarCodeList || []).length}ш`;
        if (data?.errorBarCodeList?.length) {
          notifyError(message);
        } else {
          notifySuccess(message);
        }
        const list = selected.filter(
          (itm: any) => (data?.successBarcodeList || []).indexOf(itm.barcode) < 0
        );
        setSelected(list);
        fetchOrdersList();
      } catch (error: any) {
        notifyError(error.message);
      } finally {
        setDisabled(false);
      }
    },
    [location, selected]
  );

  const fetchOrdersList = useCallback(async () => {
    setState({
      loading: true,
      result: state.result || {},
    });
    try {
      const { result } = await API.get({ apiVersion: 'api' })(
        `/order/list${location.search}&status=${parcelStatusCd}`
      );
      setState({
        loading: false,
        result,
      });
    } catch (error: any) {
      notifyError(error.message);
    }
  }, [location, state, parcelStatusCd]);

  const download = useCallback(async () => {
    setDownloadLoading(true);
    await orderDownload(location.search, parcelStatusCd!);
    setDownloadLoading(false);
  }, [location, parcelStatusCd]);

  const batchPrintOrder = useCallback(() => {
    try {
      setPrintLoading(true);
      const orderIds: number[] = [...new Set(selected.map((itm) => itm.order.no))];
      setPrint({
        visible: true,
        orderIds: orderIds,
      });
    } catch (error: any) {
      notifyError(error.message);
    } finally {
      setPrintLoading(false);
    }
  }, [selected]);

  useEffect(() => {
    if (location.search) {
      fetchOrdersList();
    }
  }, [location]);

  useEffect(() => {
    if (!location.search) {
      const params = parseSearchParam({});
      navigate(`${location.pathname}?${queryString.stringify(params)}`);
    }
  }, [location]);

  const handleTableChange = useCallback(
    (pagination: TablePaginationConfig) => {
      const params = queryString.parse(location.search);
      const search = {
        ...params,
        page: pagination.current || 1,
        size: pagination.pageSize || 10,
      };
      navigate(`${location.pathname}?${queryString.stringify(search)}`);
    },
    [location, navigate]
  );

  const columns = useMemo(() => {
    const items: any = [
      {
        title: '#',
        key: 'shipmentOrderNo',
        width: 100,
        fixed: 'left',
        render: (val: number, row: any) => (
          <Link to={`/order/detail/${row.order?.no}`}>#{row.order?.no}</Link>
        ),
      },
      {
        title: 'Скан огноо',
        width: 100,
        fixed: 'left',
        render: (val: string, row: any) => (
          <span>{dayjs(row.scanDate).format('YYYY-MM-DD HH:mm')}</span>
        ),
      },
      {
        title: 'Хугацаа',
        width: 120,
        render: (val: any, row: any) => {
          if (!row?.receiverDate || !row?.completeDate) {
            return <span></span>;
          }
          const now = dayjs();
          const complete = dayjs(row.completeDate);
          const totalMinutes = complete.diff(now, 'minute');
          const abs = Math.abs(totalMinutes);
          const h = Math.floor(abs / 60);
          const m = abs % 60;
          if (totalMinutes < 0) {
            return (
              <span style={{ color: '#ff4d4f', fontWeight: 600 }}>
                {h}ц {m}м хэтэрсэн
              </span>
            );
          }
          const color = h < 6 ? '#fa8c16' : '#52c41a';
          return (
            <span style={{ color, fontWeight: 500 }}>
              {h}ц {m}м үлдсэн
            </span>
          );
        },
      },
      {
        title: 'Скан салбар',
        width: 120,
        render: (val: any, row: any) => <span>{row?.scanBranch?.name}</span>,
      },
      {
        title: 'Скан хүргэгч',
        width: 150,
        render: (val: any, row: any) => <span>{row?.scanMember?.firstName}</span>,
      },
      {
        title: 'Авах салбар',
        width: 120,
        render: (val: any, row: any) => <span>{row?.order?.lastBranch?.name}</span>,
      },
      {
        title: 'Хүлээн авагч',
        render: (val: any, row: any) => (
          <div>
            <span style={{ display: 'block' }}>
              {row?.order?.receiverFname +
                ' ' +
                row?.order?.receiverPhone1 +
                (row?.order?.receiverPhone2 ? ', ' + row?.order?.receiverPhone2 : '')}
            </span>
            <span>
              {row?.order?.receiverProvinceName} {row?.order?.receiverDistrictName}{' '}
              {row?.order?.receiverAddressDetail}
            </span>
          </div>
        ),
      },
      {
        title: 'Хүргэлт',
        width: 80,
        render: (val: any, row: any) => <span>{row.order?.deliveryType?.text}</span>,
      },
      {
        title: 'Хэмжээ',
        dataIndex: 'weightType',
        width: 80,
        render: (val: any) => <span>{val?.name}</span>,
      },
      {
        title: 'Дүн',
        dataIndex: 'price',
        width: 80,
        render: (val: number) => <span>{priceFormat(val)}₮</span>,
      },
      {
        title: 'Харилцагч',
        width: 100,
        render: (val: any, row: any) => <span>{row.order?.customer?.name} </span>,
      },
      {
        title: 'Баримт ',
        width: 120,
        render: (val: any, row: any) => <div>{row?.barcode}</div>,
      },
    ];

    if (!getCustomer) {
      items.push({
        title: 'Үйлдэл',
        key: 'action',
        width: 90,
        fixed: 'right',
        render: (val: any, row: any) => (
          <div>
            <Button
              size="small"
              type="default"
              icon={<EditOutlined />}
              onClick={() => navigate(`/order/update/${row.order?.no}`)}
            />
          </div>
        ),
      });
    }

    return items;
  }, [getCustomer]);

  const selectedRowKeys = useMemo(() => {
    return selected.map((itm) => itm.no);
  }, [selected]);

  const rowSelection: TableProps<any>['rowSelection'] = {
    onChange: (selectedRowKeys: React.Key[], selectedRows: any[]) => {
      setSelected(selectedRows);
    },
    selectedRowKeys,
    getCheckboxProps: (record: any) => ({
      disabled: record.name === 'Disabled User',
      name: record.name,
    }),
    preserveSelectedRowKeys: true,
  };

  const nextStatus = useMemo(() => {
    return nextStatusList(statusList, parcelStatusCd || '');
  }, [statusList, parcelStatusCd]);

  const parcelStatus = useMemo(() => {
    return statusList.find((status: any) => status.cd === parcelStatusCd?.toUpperCase());
  }, [statusList, parcelStatusCd]);

  return (
    <Fragment>
      <Flex vertical gap={24}>
        {!getCustomer ? (
          <Flex gap={24} justify="start">
            <Space.Compact block>
              {(nextStatus || []).map((item: any) => {
                return (
                  <Button
                    type="default"
                    onClick={() => batchChangeOrder(item.cd)}
                    disabled={!(selected.length > 0) || disabled}
                  >
                    {item.scanName} {selected.length > 0 ? ' (' + selected.length + ')' : ''}
                  </Button>
                );
              })}
              <Button
                type="default"
                loading={downloadLoading}
                onClick={() => updateScan({ visible: true, parcelStatus })}
              >
                Скан хийх
              </Button>
              <Button type="default" loading={downloadLoading} onClick={() => download()}>
                Эксел татах
              </Button>
              <Button
                type="default"
                icon={<PrinterOutlined />}
                onClick={() => batchPrintOrder()}
                disabled={!(selected.length > 0)}
                loading={printLoading}
              >
                Баримт хэвлэх {selected.length > 0 ? ' (' + selected.length + ')' : ''}
              </Button>
            </Space.Compact>
            {parcelStatusCd === 'received' ||
            parcelStatusCd === 'delivery_started' ||
            parcelStatusCd === 'return_delivery_started' ||
            parcelStatusCd === 'return_delivery_completed' ||
            parcelStatusCd === 'sent_to_branch' ||
            parcelStatusCd === 'sent_from_tml' ? (
              <Button type="default" icon={<PrinterOutlined />} onClick={() => setSecondory(true)}>
                Дагавар хэвлэх
              </Button>
            ) : null}
          </Flex>
        ) : null}
        <Flex>
          <div>
            Нийт тоо: <b>{state?.result?.total}ш</b> Нийт дүн:{' '}
            <b>{priceFormat(state?.result?.amount || 0)}₮</b>
          </div>
        </Flex>
      </Flex>
      <style>{`.row-overdue td { background: #fff1f0 !important; }`}</style>
      <Table
        rowKey={(record: any) => record.no}
        size="small"
        scroll={{ x: 1340 }}
        loading={state.loading}
        columns={columns}
        rowSelection={rowSelection}
        rowClassName={(record: any) =>
          record.completeDate && dayjs(record.completeDate).isBefore(dayjs()) ? 'row-overdue' : ''
        }
        pagination={{
          current: state?.result?.page || 1,
          size: 'small',
          pageSize: state?.result?.size || 10,
          total: state?.result?.total,
          position: ['bottomRight', 'topRight'],
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
        dataSource={state.result.data || []}
      />
      <ParcelOrderPrint
        visible={print.visible}
        orderIds={print.orderIds}
        onClose={() => {
          setPrint({ visible: false, orderIds: [] });
          setSelected([]);
        }}
        onReload={() => fetchOrdersList()}
      />
      <ParcelOrderScanModal
        visible={scan.visible}
        parcelStatus={scan.parcelStatus}
        items={scan?.items}
        onClose={() => updateScan({ visible: false })}
        onReload={() => fetchOrdersList()}
      />
      <ParcelOrderSecondoryPrint
        selected={selected}
        visible={secondory}
        onClose={() => setSecondory(false)}
      />
    </Fragment>
  );
};
